import unitConversionService from './pixelToMeters.js';
import { auth } from '../firebase/config';

class CameraPredictionService {
  constructor() {
    // Check if we're in development by looking at the URL
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Check for URL parameter override (e.g., ?useRemoteAPIs=true)
    const urlParams = new URLSearchParams(window.location.search);
    const useRemoteAPIs = urlParams.get('useRemoteAPIs') === 'true';
    
    const envSuffix = import.meta.env.PROD ? 'prod' : 'dev';
    this.apiUrl = (isDevelopment && !useRemoteAPIs)
      ? 'http://localhost:8001'  // Local camera predictor API
      : `https://camera-predictor-api-${envSuffix}.blackbay-6bd764e8.eastus2.azurecontainerapps.io`;
  }

  /**
   * Predict camera parameters from keypoints using server-side API
   */
  async predict(keypoints, width, height) {
    try {
      // Get Firebase ID token
      const user = auth.currentUser;
      const idToken = user ? await user.getIdToken() : null;

      const headers = { 'Content-Type': 'application/json' };
      if (idToken) {
        headers['X-Firebase-Token'] = idToken;
      }

      // Call the API
      const apiEndpoint = `${this.apiUrl}/api/predict`;
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          keypoints: keypoints,
          width: width,
          height: height
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API call failed: ${response.status} - ${errorText.substring(0, 200)}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Camera parameter prediction failed');
      }

      const cameraParameters = result.camera_parameters;
      
      // Convert pixels to meters for both linear and equisolid models
      const linearConversion = await unitConversionService.convert(
        keypoints, 
        cameraParameters.linear, 
        width, 
        height
      );
      
      const equisolidConversion = await unitConversionService.convert(
        keypoints, 
        cameraParameters.equisolid, 
        width, 
        height
      );
      
      // Compare distances to reference court
      const linearDistance = linearConversion.distance_to_reference_court;
      const equisolidDistance = equisolidConversion.distance_to_reference_court;
      
      // Determine which model is closer to the reference court
      const bestModel = linearDistance < equisolidDistance ? 'linear' : 'equisolid';
      const bestDistance = Math.min(linearDistance, equisolidDistance);
      
      // Add conversion results to the response
      cameraParameters.linear.real_world_keypoints = linearConversion.real_world_keypoints;
      cameraParameters.linear.distance_to_reference_court = linearDistance;
      cameraParameters.equisolid.real_world_keypoints = equisolidConversion.real_world_keypoints;
      cameraParameters.equisolid.distance_to_reference_court = equisolidDistance;
      cameraParameters.best_model = bestModel;
      cameraParameters.best_distance = bestDistance;

      return cameraParameters;
      
    } catch (error) {
      console.error('Camera parameter prediction failed:', error);
      throw error;
    }
  }

  /**
   * Health check for the camera predictor API
   */
  async healthCheck() {
    try {
      const response = await fetch(`${this.apiUrl}/health`);
      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Camera predictor API health check failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const cameraPredictionService = new CameraPredictionService();
